/**
 * Environment Configuration Composable
 *
 * Provides centralized environment detection and configuration
 * for visual indicators across the application.
 *
 * Industry-Standard Environment Naming:
 * ┌─────────────┬────────────────────────────────────────────────────┐
 * │ Environment │ Purpose                                            │
 * ├─────────────┼────────────────────────────────────────────────────┤
 * │ local       │ Developer machine only (not shared)                │
 * │ dev         │ Shared integration environment for developers      │
 * │ preview     │ Per-branch/PR validation (ephemeral)               │
 * │ test        │ Automated testing / QA environment                 │
 * │ staging     │ Pre-production validation (mirrors production)     │
 * │ uat         │ User Acceptance Testing (formal sign-off)          │
 * │ production  │ Real users / customers                             │
 * └─────────────┴────────────────────────────────────────────────────┘
 *
 * Project-Specific Environments:
 * - compose: Docker Compose local development
 * - android: Android mobile development build
 * - ios: iOS mobile development build
 */

import { computed } from 'vue';

// Environment configuration interface
export interface EnvironmentConfig {
  name: string;
  display: string;
  color: string;
  darkColor: string;
  icon: string;
  showBanner: boolean;
  bannerDismissible: boolean;
  /** Short banner text (< 1 second to read) */
  bannerText: string;
  /** Detailed tooltip explanation */
  tooltipDetails: string[];
  showInSidebar: boolean;
  showInFavicon: boolean;
}

// =============================================================================
// CORE ENVIRONMENT CONFIGURATIONS
// =============================================================================

const ENV_CONFIGS: Record<string, EnvironmentConfig> = {
  // -------------------------------------------------------------------------
  // LOCAL - Developer machine only (not shared)
  // -------------------------------------------------------------------------
  local: {
    name: 'local',
    display: 'Local',
    color: '#6B7280', // Gray
    darkColor: '#9CA3AF', // lightened for dark surfaces (was #4B5563, 2.16:1)
    icon: 'computer',
    showBanner: true,
    bannerDismissible: true,
    bannerText: 'Local environment - Not connected to shared services',
    tooltipDetails: [
      'Data is stored locally and not synced',
      'External integrations may be mocked or disabled',
      'Performance may differ from production',
      'For individual development only',
    ],
    showInSidebar: true,
    showInFavicon: true,
  },

  // -------------------------------------------------------------------------
  // DEV - Shared integration environment for developers
  // -------------------------------------------------------------------------
  dev: {
    name: 'dev',
    display: 'Dev',
    color: '#F97316', // Orange
    darkColor: '#EA580C',
    icon: 'code',
    showBanner: true,
    bannerDismissible: true,
    bannerText: 'Development environment - Test data only',
    tooltipDetails: [
      'Data may be deleted or reset at any time without notice',
      'Availability and performance are not guaranteed',
      'Features may be incomplete or unstable',
      'Emails and notifications may be disabled',
      'Do not use for real users or real data',
    ],
    showInSidebar: true,
    showInFavicon: true,
  },

  // -------------------------------------------------------------------------
  // PREVIEW - Per-branch/PR validation (modern ephemeral environments)
  // -------------------------------------------------------------------------
  preview: {
    name: 'preview',
    display: 'Preview',
    color: '#8B5CF6', // Purple
    darkColor: '#A78BFA', // lightened for dark surfaces (was #7C3AED, 2.87:1)
    icon: 'visibility',
    showBanner: true,
    bannerDismissible: true,
    bannerText: 'Preview environment - For testing only',
    tooltipDetails: [
      'This environment is temporary and may be deleted',
      'Data will not be preserved after review',
      'Features may behave differently than production',
      'External integrations may be simulated',
      'For feature validation and review only',
    ],
    showInSidebar: true,
    showInFavicon: true,
  },

  // -------------------------------------------------------------------------
  // TEST - Automated testing / QA environment
  // -------------------------------------------------------------------------
  test: {
    name: 'test',
    display: 'Test',
    color: '#3B82F6', // Blue
    darkColor: '#60A5FA', // lightened for dark surfaces (was #2563EB, 3.16:1)
    icon: 'science',
    showBanner: true,
    bannerDismissible: false, // Cannot dismiss in test
    bannerText: 'Test environment - QA validation only',
    tooltipDetails: [
      'Data may be deleted or reset at any time without notice',
      'No SLA or availability guarantees',
      'Payments and external services are simulated',
      'Emails are not sent to real recipients',
      'For QA testing and validation only',
    ],
    showInSidebar: true,
    showInFavicon: true,
  },

  // -------------------------------------------------------------------------
  // SANDBOX - Isolated playground for demos and exploratory testing
  // -------------------------------------------------------------------------
  sandbox: {
    name: 'sandbox',
    display: 'Sandbox',
    color: '#14B8A6', // Teal
    darkColor: '#0D9488',
    icon: 'science',
    showBanner: true,
    bannerDismissible: false,
    bannerText: 'Sandbox environment - Exploratory testing only',
    tooltipDetails: [
      'Data may be deleted or reset at any time without notice',
      'No SLA or availability guarantees',
      'Payments and external services are simulated',
      'Emails are not sent to real recipients',
      'For demos and exploratory testing only',
    ],
    showInSidebar: true,
    showInFavicon: true,
  },

  // -------------------------------------------------------------------------
  // STAGING - Pre-production validation (mirrors production)
  // -------------------------------------------------------------------------
  staging: {
    name: 'staging',
    display: 'Staging',
    color: '#F59E0B', // Amber/Yellow
    darkColor: '#D97706',
    icon: 'published_with_changes',
    showBanner: true,
    bannerDismissible: false, // Cannot dismiss in staging
    bannerText: 'Staging environment - Pre-production data',
    tooltipDetails: [
      'Data may be reset to match production periodically',
      'Changes here do not affect production',
      'Performance characteristics may differ',
      'External integrations may use sandbox modes',
      'Final validation before production release',
    ],
    showInSidebar: true,
    showInFavicon: true,
  },

  // -------------------------------------------------------------------------
  // UAT - User Acceptance Testing (formal sign-off)
  // -------------------------------------------------------------------------
  uat: {
    name: 'uat',
    display: 'UAT',
    color: '#EC4899', // Pink
    darkColor: '#DB2777',
    icon: 'how_to_reg',
    showBanner: true,
    bannerDismissible: false, // Cannot dismiss in UAT
    bannerText: 'UAT environment - Awaiting sign-off',
    tooltipDetails: [
      'Data may be deleted after acceptance testing',
      'This environment mirrors production configuration',
      'Payments and external services are simulated',
      'For user acceptance testing and sign-off only',
      'Do not use for real transactions',
    ],
    showInSidebar: true,
    showInFavicon: true,
  },

  // -------------------------------------------------------------------------
  // PRODUCTION - Real users / customers (never show indicators)
  // -------------------------------------------------------------------------
  production: {
    name: 'production',
    display: 'Production',
    color: '#10B981', // Green
    darkColor: '#059669',
    icon: 'verified',
    showBanner: false,
    bannerDismissible: false,
    bannerText: '',
    tooltipDetails: [],
    showInSidebar: false,
    showInFavicon: false,
  },

  // =========================================================================
  // PROJECT-SPECIFIC ENVIRONMENTS
  // =========================================================================

  // -------------------------------------------------------------------------
  // COMPOSE - Docker Compose local development
  // -------------------------------------------------------------------------
  compose: {
    name: 'compose',
    display: 'Compose',
    color: '#0EA5E9', // Sky blue (Docker color)
    darkColor: '#0284C7',
    icon: 'layers',
    showBanner: true,
    bannerDismissible: true,
    bannerText: 'Docker Compose - Local container environment',
    tooltipDetails: [
      'Data is stored in local containers only',
      'Containers may be reset or rebuilt at any time',
      'Services may differ from cloud configuration',
      'For local development and testing only',
    ],
    showInSidebar: true,
    showInFavicon: true,
  },

  // -------------------------------------------------------------------------
  // ANDROID - Android mobile development build
  // -------------------------------------------------------------------------
  android: {
    name: 'android',
    display: 'Android',
    color: '#3DDC84', // Android green
    darkColor: '#2BC866',
    icon: 'android',
    showBanner: true,
    bannerDismissible: true,
    bannerText: 'Android build - Development version',
    tooltipDetails: [
      'This is a development build, not for distribution',
      'Data may be deleted when app is reinstalled',
      'Push notifications may be disabled',
      'Features may be incomplete or unstable',
      'For mobile development testing only',
    ],
    showInSidebar: true,
    showInFavicon: true,
  },

  // -------------------------------------------------------------------------
  // iOS - iOS mobile development build
  // -------------------------------------------------------------------------
  ios: {
    name: 'ios',
    display: 'iOS',
    color: '#007AFF', // Apple blue
    darkColor: '#0A84FF',
    icon: 'phone_iphone',
    showBanner: true,
    bannerDismissible: true,
    bannerText: 'iOS build - Development version',
    tooltipDetails: [
      'This is a development build, not for distribution',
      'Data may be deleted when app is reinstalled',
      'Push notifications may be disabled',
      'Features may be incomplete or unstable',
      'For mobile development testing only',
    ],
    showInSidebar: true,
    showInFavicon: true,
  },
};

// =============================================================================
// ENVIRONMENT ALIASES
// Maps common synonyms to canonical environment names
// =============================================================================

const ENV_ALIASES: Record<string, string> = {
  // Development aliases
  development: 'dev',
  develop: 'dev',

  // Production aliases
  prod: 'production',
  live: 'production',

  // Test aliases
  testing: 'test',
  qa: 'test',

  // Staging aliases
  stage: 'staging',
  preprod: 'staging',
  'pre-prod': 'staging',

  // UAT aliases
  acceptance: 'uat',
  'user-acceptance': 'uat',

  // Preview aliases
  review: 'preview',
  pr: 'preview',
  branch: 'preview',

  // Local aliases
  localhost: 'local',
};

/**
 * Resolves an environment name to its canonical form
 */
function resolveEnvironment(env: string): string {
  // Native flavor prefixes (android.test, ios.production) describe the
  // platform, not the environment — strip them before alias resolution so
  // native non-production builds get their badge/banner config instead of
  // silently falling back to the production (indicator-free) config.
  const normalized = env
    .toLowerCase()
    .trim()
    .replace(/^(android|ios)\./, '');
  return ENV_ALIASES[normalized] || normalized;
}

export interface UseEnvironmentConfigOptions {
  /**
   * Current environment string. Defaults to `import.meta.env.VITE_APP_ENV`
   * (the convention both UI and SI follow), falling back to 'production'.
   * Pass explicitly only if you need to override that source.
   */
  appEnv?: string;
}

/**
 * Composable for environment configuration
 */
export function useEnvironmentConfig(options: UseEnvironmentConfigOptions = {}) {
  // Get current environment and resolve aliases
  const rawEnv =
    options.appEnv ||
    (import.meta.env.VITE_APP_ENV as string | undefined) ||
    'production';
  const currentEnv = resolveEnvironment(rawEnv);

  // Get configuration for current environment (guaranteed to exist, fallback to production)
  const config = (ENV_CONFIGS[currentEnv] || ENV_CONFIGS.production) as EnvironmentConfig;

  /**
   * Check if environment is production
   */
  const isProduction = computed(() => currentEnv.toLowerCase() === 'production');

  /**
   * Check if sidebar badge should be shown
   */
  const shouldShowSidebarBadge = computed(() => {
    return config.showInSidebar && !isProduction.value;
  });

  /**
   * Check if favicon badge should be shown
   */
  const shouldShowFaviconBadge = computed(() => {
    return config.showInFavicon && !isProduction.value;
  });

  /**
   * Get environment color (respects dark mode)
   */
  function getEnvironmentColor(isDark: boolean): string {
    return isDark ? config.darkColor : config.color;
  }

  return {
    // Environment info
    currentEnv,
    config,
    isProduction,

    // Display flags
    shouldShowSidebarBadge,
    shouldShowFaviconBadge,

    // Utilities
    getEnvironmentColor,
  };
}
